/**
 * useFeaturesPageSeo — SEO view-model for the marketing Features page:
 * the brand read, the memoized JSON-LD (`webPage` + `breadcrumbList`) and the
 * OG card URL.
 *
 * WHY THIS SEAM EXISTS: `<FeaturesPage>`'s jsonLd `useMemo` depends only on
 * `brand`, which comes from `getBrand()` — a plain registry read, not a hook.
 * `web/component-state-order` therefore saw a dependency-free computed hook
 * sitting after the `useTranslation` / `useAnalytics` custom hooks and demanded
 * it move up. It could not simply be hoisted in place: the page's scroll-spy
 * `useEffect` has an EMPTY dep array, so once the memo moved above the custom
 * hooks the linter would (correctly, by its own rules) then demand that
 * dep-free effect move above the memo too — which would swap the execution
 * order of the page's two mount effects. Relocating the memo into a custom hook
 * removes the computed group from the page entirely, so every group pair the
 * rule checks becomes vacuous and **both mount effects keep their original
 * order**. Zero behavior change, zero suppression.
 *
 * @module portals/marketing-website/hooks/useFeaturesPageSeo
 */

import { useMemo } from 'react';
import { getBrand } from '@puffin-core/brand-assets';
import { webPage, breadcrumbList } from '@puffin-core/marketing-shell';

const APP_KEY = 'purchase';
const FEATURES_TITLE = 'Features - PurchasePuffin | Custom E-Commerce Platform';
const FEATURES_DESCRIPTION =
  'Explore PurchasePuffin features: product customization, headless storefronts, dynamic pricing, and partner portals.';
const FEATURES_CANONICAL = 'https://purchasepuffin.com/features';

/**
 * @returns {{
 *   title: string,
 *   description: string,
 *   canonical: string,
 *   jsonLd: Array<Object>,
 *   ogImageUrl: string,
 * }}
 */
export function useFeaturesPageSeo() {
  const brand = getBrand(APP_KEY);

  const jsonLd = useMemo(
    () => [
      webPage({
        appKey: APP_KEY,
        brand,
        url: FEATURES_CANONICAL,
        title: FEATURES_TITLE,
        description: FEATURES_DESCRIPTION,
      }),
      breadcrumbList({
        crumbs: [
          { name: 'Home', url: 'https://purchasepuffin.com/' },
          { name: 'Features', url: FEATURES_CANONICAL },
        ],
      }),
    ],
    [brand],
  );

  const ogImageUrl = `https://purchasepuffin.com${brand.assets.ogCard}`;

  return {
    title: FEATURES_TITLE,
    description: FEATURES_DESCRIPTION,
    canonical: FEATURES_CANONICAL,
    jsonLd,
    ogImageUrl,
  };
}

export default useFeaturesPageSeo;
